import { useEffect, useRef, useState } from 'react';
import { ListPlus } from 'lucide-react';
import { CARD_IMPORT_MAX, hasSourceColumns, type CardSummary, type ParsedImport } from '@kaba/shared';
import { importGateFailures, openCardImport, runCardImport } from '../../lib/cardImport';
import { useCreateCard } from '../../lib/board';
import { Button } from '../ui/ui';

const n = (x: number, one: string, many: string) => `${x.toLocaleString('de-DE')} ${x === 1 ? one : many}`;

/**
 * 0.88: Rückfrage im Feld „Karte hinzufügen“, wenn mehrere Aufgaben eingefügt wurden:
 * „7 Karten anlegen“ · „Prüfen …“ (Dialog mit Vorschau) · „Als eine Karte“.
 */
export function PasteTasksPrompt({
  boardId,
  columnId,
  parsed,
  text,
  placement = 'bottom',
  onCreated,
  onDone,
  onSingle,
  onCancel,
}: {
  boardId: string;
  columnId: string;
  parsed: ParsedImport;
  text: string;
  placement?: 'top' | 'bottom';
  onCreated?: (card: CardSummary) => void;
  /** Karten sind angelegt (oder der Dialog übernimmt) */
  onDone: () => void;
  /** doch eine einzige Karte */
  onSingle: () => void;
  /** zurück zum Text */
  onCancel: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const primary = useRef<HTMLButtonElement>(null);
  const create = useCreateCard(boardId);
  useEffect(() => primary.current?.focus(), []);
  const { plan, warnings } = parsed;
  const count = plan.cards.length;
  const tooMany = count > CARD_IMPORT_MAX;
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  const withLabels = plan.cards.filter((c) => c.labelKeys.length).length;
  const withDue = plan.cards.filter((c) => c.dueAt).length;
  const withChecklist = plan.cards.filter((c) => c.checklist.length).length;
  const headings = hasSourceColumns(parsed);
  const details = [
    withLabels ? `${n(withLabels, 'Karte', 'Karten')} mit Label` : '',
    withDue ? `${withDue} mit Frist` : '',
    withChecklist ? `${withChecklist} mit Checkliste` : '',
  ].filter(Boolean);

  const createAll = async () => {
    setError(null);
    if (offline) {
      // Ohne Verbindung: einzeln über die Warteschlange, nur Titel
      for (const c of placement === 'top' ? [...plan.cards].reverse() : plan.cards) create.mutate({ columnId, title: c.title, placement }, { onSuccess: (card) => onCreated?.(card) });
      onDone();
      return;
    }
    setBusy(true);
    try {
      await runCardImport({ boardId, plan, source: 'paste', columnId, placement }, onCreated);
      onDone();
    } catch (err) {
      const gates = importGateFailures(err);
      setError(gates ? `${(err as Error).message} Mit „Prüfen …“ kannst du eine andere Spalte wählen.` : (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="group"
      aria-label="Mehrere Aufgaben erkannt"
      className="flex flex-col gap-2 rounded-xl border border-accent bg-[var(--panel)] p-2.5 text-sm shadow-sm"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onCancel();
        }
      }}
    >
      <p className="flex items-center gap-1.5 font-medium">
        <ListPlus size={16} aria-hidden /> {n(count, 'Aufgabe', 'Aufgaben')} erkannt
      </p>
      <ol className="list-inside list-decimal text-[var(--ink-soft)]" data-testid="paste-preview">
        {plan.cards.slice(0, 5).map((c) => (
          <li key={c.key} className="truncate">
            {c.title}
          </li>
        ))}
      </ol>
      {count > 5 && <p className="text-xs text-[var(--ink-soft)]">… und {n(count - 5, 'weitere', 'weitere')}</p>}
      {details.length > 0 && !offline && <p className="text-xs text-[var(--ink-soft)]">{details.join(' · ')}</p>}
      {headings && !offline && <p className="text-xs text-[var(--ink-soft)]">Überschriften kommen hier nicht als Spalten mit – das geht über „Prüfen …“.</p>}
      {warnings.map((w) => (
        <p key={w} className="text-xs text-amber-800 dark:text-amber-200">
          {w}
        </p>
      ))}
      {offline && <p className="text-xs text-amber-800 dark:text-amber-200">Ohne Verbindung werden nur die Titel übernommen (keine Labels, Termine, Checklisten).</p>}
      {tooMany && <p className="text-xs text-amber-800 dark:text-amber-200">Höchstens {CARD_IMPORT_MAX} Karten auf einmal – bitte die Liste teilen.</p>}
      {error && (
        <p role="alert" className="rounded-lg bg-rose-50 p-2 text-xs text-rose-700 dark:bg-rose-950 dark:text-rose-200">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="button" ref={primary} variant="primary" disabled={busy || tooMany} onClick={() => void createAll()}>
          {busy ? 'Lege an …' : `${n(count, 'Karte', 'Karten')} anlegen`}
        </Button>
        {!offline && (
          <Button
            type="button"
            onClick={() => {
              openCardImport({ boardId, columnId, text });
              onDone();
            }}
          >
            Prüfen …
          </Button>
        )}
        <Button type="button" variant="ghost" onClick={onSingle}>
          Als eine Karte
        </Button>
      </div>
    </div>
  );
}
